import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
    IRootCauseAnalysis,
    IRootCauseAnalysisLabels,
    IRCAFinding,
    IRootCauseAnalysisControls,
} from "."

export interface IInitializeRootCauseAnalysis {
    findings: IRCAFinding[]
    controls: Partial<IRootCauseAnalysisControls>
    labels: IRootCauseAnalysisLabels
}

export const incidentsSlice = createSlice({
    name: "incidents",

    initialState: {
        controls: {
            isCopyAllowed: false,
            isReadOnly: false,
            isFiveWhysEnabled: false,
            isIshikawaEnabled: false,
        },
        findings: [] as IRCAFinding[],
        labels: {
            addFiveWhysFinding: "",
            addFiveWhysRCA: "",
            addIshikawaFinding: "",
            addIshikawaRCA: "",
            addNewFinding: "",
            addParentFinding: "",
            clear: "",
            contextMenu: "",
            copyAnalysis: "",
            delete: "",
            exceptionContentCannotBeDeleted: "",
            markAsRootCause: "",
            or: "",
            placeholderFiveWhys: "",
            placeholderIshikawa: "",
            startOver: "",
            unmarkAsRootCause: "",
        },
        isInitialized: false,
    } as IRootCauseAnalysis,

    reducers: {
        initialize: (
            state,
            action: PayloadAction<IInitializeRootCauseAnalysis>,
        ) => {
            if (!state.isInitialized) {
                const { controls, findings, labels } = action.payload
                state.controls = {
                    isFiveWhysEnabled: controls.isFiveWhysEnabled ?? false,
                    isIshikawaEnabled: controls.isIshikawaEnabled ?? false,
                    isReadOnly: controls.isReadOnly ?? false,
                    isCopyAllowed: hasRootCause(findings),
                }
                state.findings = findings
                state.labels = labels
                state.isInitialized = true
            }
        },
        loadData: (state, action: PayloadAction<IRCAFinding[]>) => {
            state.findings = action.payload
            state.controls.isCopyAllowed = hasRootCause(state.findings)
        },
    },
})

const hasRootCause = (findings: IRCAFinding[]): boolean => {
    // guard clause
    if (findings.length === 0) {
        return false
    }

    for (const finding of findings) {
        if (hasRootCauseInFinding(finding)) {
            return true
        }
    }

    return false
}

const hasRootCauseInFinding = (finding: IRCAFinding | null): boolean => {
    // guard clause
    if (!finding) {
        return false
    }

    // check current node
    if (finding.isMarkedAsRootCause) {
        return true
    }

    // check children recursively
    if (finding.children) {
        for (const child of finding.children) {
            if (hasRootCauseInFinding(child)) {
                return true
            }
        }
    }

    return false
}

export default incidentsSlice.reducer

export const { initialize, loadData } = incidentsSlice.actions
